import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Button } from './ui/button';
import { Sheet, SheetContent, SheetTrigger } from './ui/sheet';
import { Menu, Heart, Facebook, Instagram } from 'lucide-react';
import { mockData } from '../mock';

const Header = () => {
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const { organization } = mockData;

  const navigation = [
    { name: 'Home', path: '/' },
    { name: 'About', path: '/about' },
    { name: 'Services', path: '/services' },
    { name: 'Donate', path: '/donate' },
    { name: 'KBK Basket Shop', path: '/shop-wishlist' },
    { name: 'Volunteer', path: '/volunteer' },
    { name: 'Contact', path: '/contact' }
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <header className="bg-white border-b border-mauve-300 sticky top-0 z-50 shadow-sm" data-testid="main-header">
      <div className="max-w-7xl mx-auto px-4">
        <div className="flex items-center justify-between h-16">
          {/* Logo and Name */}
          <Link to="/" className="flex items-center gap-3 hover:opacity-80 transition-opacity">
            <img 
              src="/logo.jpg" 
              alt="Kibbles & Bits of Kindness Logo" 
              className="h-12 w-12 object-contain"
            />
            <span className="text-xl md:text-2xl font-bold text-darkslate-700">
              KBK
            </span>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-1" data-testid="desktop-nav">
            {navigation.map((item) => (
              <Link key={item.path} to={item.path}>
                <Button
                  variant="ghost"
                  className={`${
                    isActive(item.path)
                      ? 'text-red-600 bg-red-50 hover:bg-red-100'
                      : 'text-darkslate-600 hover:text-red-600 hover:bg-mauve-50'
                  } text-sm font-medium`}
                  data-testid={`nav-${item.path.replace('/', '') || 'home'}`}
                >
                  {item.name}
                </Button>
              </Link>
            ))}
          </nav>

          {/* Social Icons - Desktop */}
          <div className="hidden md:flex items-center gap-3">
            <a
              href={organization.social.facebook}
              target="_blank"
              rel="noopener noreferrer"
              className="text-darkslate-400 hover:text-red-600 transition-colors"
              aria-label="Facebook"
            >
              <Facebook className="h-5 w-5" />
            </a>
            <a
              href={organization.social.instagram}
              target="_blank"
              rel="noopener noreferrer"
              className="text-darkslate-400 hover:text-red-600 transition-colors"
              aria-label="Instagram"
            >
              <Instagram className="h-5 w-5" />
            </a>
          </div>

          {/* Mobile Menu */}
          <Sheet open={isOpen} onOpenChange={setIsOpen}>
            <SheetTrigger asChild className="md:hidden">
              <Button variant="ghost" size="icon" data-testid="mobile-menu-btn">
                <Menu className="h-6 w-6 text-darkslate-600" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-72">
              <div className="flex flex-col gap-4 mt-8">
                <div className="flex items-center gap-3 mb-4 pb-4 border-b border-mauve-200">
                  <img 
                    src="/logo.jpg" 
                    alt="KBK Logo" 
                    className="h-10 w-10 object-contain"
                  />
                  <span className="text-lg font-bold text-darkslate-700">
                    KBK
                  </span>
                </div>
                {navigation.map((item) => (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={() => setIsOpen(false)}
                    className={`px-4 py-2 rounded-lg transition-colors ${
                      isActive(item.path)
                        ? 'bg-red-50 text-red-600 font-medium'
                        : 'text-darkslate-600 hover:bg-mauve-50'
                    }`}
                  >
                    {item.name}
                  </Link>
                ))}
                <div className="flex gap-3 mt-4 pt-4 border-t border-mauve-200 px-4">
                  <a
                    href={organization.social.facebook}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-darkslate-400 hover:text-red-600 transition-colors"
                    aria-label="Facebook"
                  >
                    <Facebook className="h-5 w-5" />
                  </a>
                  <a
                    href={organization.social.instagram}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-darkslate-400 hover:text-red-600 transition-colors"
                    aria-label="Instagram"
                  >
                    <Instagram className="h-5 w-5" />
                  </a>
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
};

export default Header;
